// src/components/modules/TAI/PlanillaTAIEstado.js

import React from "react";
import PropTypes from "prop-types";
import { Tag, Tooltip, Space } from "antd";
import {
  FileTextOutlined,
  SendOutlined,
  ShoppingOutlined,
  CarOutlined,
  InboxOutlined,
  DollarOutlined,
  StopOutlined,
} from "@ant-design/icons";

// Configuración de estados de planilla TAI
const ESTADO_CONFIG = {
  generada: {
    color: "#8c8c8c",
    bg: "#fafafa",
    label: "Generada",
    icon: <FileTextOutlined />,
    description: "Planilla creada, pendiente de envío",
  },
  enviada: {
    color: "#1890ff",
    bg: "#e6f7ff",
    label: "Enviada",
    icon: <SendOutlined />,
    description: "Planilla enviada al paciente",
  },
  canjeada: {
    color: "#faad14",
    bg: "#fffbe6",
    label: "Canjeada",
    icon: <ShoppingOutlined />,
    description: "Canjeada por tiquete de transporte",
  },
  usada: {
    color: "#13c2c2",
    bg: "#e6fffb",
    label: "Usada",
    icon: <CarOutlined />,
    description: "Servicio de transporte realizado",
  },
  recibida: {
    color: "#52c41a",
    bg: "#f6ffed",
    label: "Recibida",
    icon: <InboxOutlined />,
    description: "Planilla física recibida en oficina",
  },
  facturada: {
    color: "#722ed1",
    bg: "#f9f0ff",
    label: "Facturada",
    icon: <DollarOutlined />,
    description: "Facturada a la EPS",
  },
  anulada: {
    color: "#ff4d4f",
    bg: "#fff1f0",
    label: "Anulada",
    icon: <StopOutlined />,
    description: "Planilla anulada",
  },
};

/**
 * Componente para mostrar el estado de una planilla TAI
 * Puede usarse en tablas, cards, o cualquier lugar donde se necesite mostrar el estado
 */
const PlanillaTAIEstado = ({
  estado,
  size = "default",
  showIcon = true,
  showTooltip = true,
  codigoPlanilla = null,
}) => {
  const config = ESTADO_CONFIG[estado] || ESTADO_CONFIG.generada;

  const tagContent = (
    <Tag
      color={config.color}
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}`,
        fontWeight: size === "large" ? "bold" : "normal",
        fontSize: size === "small" ? "11px" : size === "large" ? "14px" : "12px",
        padding: size === "small" ? "0 4px" : "2px 8px",
      }}
      icon={showIcon ? config.icon : null}
    >
      {config.label.toUpperCase()}
    </Tag>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={
          <div>
            <div style={{ fontWeight: "bold" }}>{config.label}</div>
            <div style={{ fontSize: "12px" }}>{config.description}</div>
            {codigoPlanilla && (
              <div style={{ fontSize: "11px", marginTop: 4 }}>
                Código: {codigoPlanilla}
              </div>
            )}
          </div>
        }
      >
        {tagContent}
      </Tooltip>
    );
  }

  return tagContent;
};

PlanillaTAIEstado.propTypes = {
  estado: PropTypes.oneOf([
    "generada",
    "enviada",
    "canjeada",
    "usada",
    "recibida",
    "facturada",
    "anulada",
  ]).isRequired,
  size: PropTypes.oneOf(["small", "default", "large"]),
  showIcon: PropTypes.bool,
  showTooltip: PropTypes.bool,
  codigoPlanilla: PropTypes.string,
};

/**
 * Componente para mostrar información completa de TAI en Control Servicios
 */
export const TAIInfoBadge = ({
  esTAI,
  transportadoraNombre,
  trayectoTipo,
  numeroTiquete,
  planillaEstado,
  codigoPlanilla,
}) => {
  if (!esTAI) {
    return (
      <Tag color="default" style={{ fontSize: "11px" }}>
        Normal
      </Tag>
    );
  }

  // Colores por tipo de trayecto
  const TRAYECTO_COLORS = {
    interveredal: "#722ed1",
    intermunicipal: "#2f54eb",
    urbano: "#13c2c2",
  };

  return (
    <Space direction="vertical" size={2}>
      <Tag
        color="#722ed1"
        style={{
          fontWeight: "bold",
          fontSize: "11px",
        }}
      >
        TAI
      </Tag>
      {trayectoTipo && (
        <Tag
          color={TRAYECTO_COLORS[trayectoTipo] || "#8c8c8c"}
          style={{ fontSize: "10px" }}
        >
          {trayectoTipo.charAt(0).toUpperCase() + trayectoTipo.slice(1)}
        </Tag>
      )}
      {transportadoraNombre && (
        <Tooltip title="Transportadora">
          <span style={{ fontSize: "10px", color: "#1890ff" }}>
            {transportadoraNombre}
          </span>
        </Tooltip>
      )}
      {numeroTiquete && (
        <span style={{ fontSize: "10px", color: "#8c8c8c" }}>
          Tiq: {numeroTiquete}
        </span>
      )}
      {planillaEstado && (
        <PlanillaTAIEstado
          estado={planillaEstado}
          size="small"
          showIcon={false}
          showTooltip={true}
          codigoPlanilla={codigoPlanilla}
        />
      )}
    </Space>
  );
};

TAIInfoBadge.propTypes = {
  esTAI: PropTypes.bool.isRequired,
  transportadoraNombre: PropTypes.string,
  trayectoTipo: PropTypes.string,
  numeroTiquete: PropTypes.string,
  planillaEstado: PropTypes.string,
  codigoPlanilla: PropTypes.string,
};

export default PlanillaTAIEstado;
